import React from "react";
import { useState, useMemo, useEffect } from "react";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, MapPin, Clock, MessageSquare, HandCoins, FileSpreadsheet, ChevronRight, PhoneCall, CheckCircle2 } from "lucide-react";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

/**
 * FAST MAX – Landing Page (Spanish)
 * ---------------------------------------------------------
 * • Single-file React component (JSX), production-ready layout
 * • TailwindCSS utility classes
 * • Lucide icons + Framer Motion micro-animations
 * • Brand colors from manual: #B1336E (primary), #E6B43C, #67A43C
 * • Sections: Navbar, Hero, Beneficios, Proceso, Tecnología, Cobertura, Resultados, CTA, Footer
 * • CTAs: Crear cuenta / Agendar entrega
 * • Language: Spanish (Peru). B2B corporate tone.
 *
 * Notes:
 * - Ensure Montserrat is loaded globally (e.g., in index.html or layout):
 *   <link rel="preconnect" href="https://fonts.googleapis.com"/>
 *   <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
 *   <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;800&display=swap" rel="stylesheet"/>
 * - Tailwind recommended config: set font-sans to Montserrat in tailwind.config.js
 * - Replace placeholder images/screenshots with your real assets
 */

const brand = {
  primary: "#B1336E",
  accentGold: "#E6B43C",
  accentGreen: "#67A43C",
};

const container = "mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const WA_NUMBER = "51977689676"; // +51 977 689 676 without symbols
const WA_MSG = encodeURIComponent("Hola Fast Max quiero  mas informacion");
const WA_HREF = `https://wa.me/${WA_NUMBER}?text=${WA_MSG}`;

export default function FastMaxLanding() {
  return (
    <div className="font-sans text-slate-900 antialiased">
      {/* Sticky Nav */}
      <header className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-white/70 bg-white/90 border-b border-slate-100">
  <nav className={`${container} flex h-16 items-center justify-between gap-3`} aria-label="Principal">
    {/* Logo */}
    <a href="" onClick={scrollTo("header")} className="flex items-center gap-2 shrink-0" aria-label="FAST MAX">
      <img
        src={`${process.env.PUBLIC_URL}/logo.png`}
        alt="FAST MAX"
        className="h-8 w-auto"
      />
      {/* Hide the text label on very small screens to save space */}
      <span className="hidden sm:inline text-lg font-extrabold tracking-wide" style={{ color: brand.primary }}>
        FAST MAX
      </span>
    </a>

    {/* Nav links (desktop only) */}
    <ul className="hidden md:flex items-center gap-8 text-sm">
      <li><a href="#beneficios" onClick={scrollTo("beneficios")} className="hover:opacity-80">Beneficios</a></li>
      <li><a href="#proceso" onClick={scrollTo("proceso")} className="hover:opacity-80">Cómo funciona</a></li>
      <li><a href="#tecnologia" onClick={scrollTo("tecnologia")} className="hover:opacity-80">Tecnología</a></li>
      <li><a href="#cobertura" onClick={scrollTo("cobertura")} className="hover:opacity-80">Cobertura</a></li>
      <li><a href="#precios" onClick={scrollTo("precios")} className="hover:opacity-80">Precios</a></li>
      <li><a href="#contacto" onClick={scrollTo("contacto")} className="hover:opacity-80">Contacto</a></li>
    </ul>

    {/* CTAs — both visible on mobile; compact sizes on small screens */}
    <div className="flex items-center gap-2 shrink-0">
      <Link
        to="/login"
        className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 whitespace-nowrap
                   sm:px-4 sm:py-2 sm:text-sm"
      >
        Iniciar sesión
      </Link>

      <Link
        to="/register"
        className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-white shadow-sm whitespace-nowrap
                   sm:px-4 sm:py-2 sm:text-sm"
        style={{ background: brand.primary }}
      >
        Crear cuenta
        <ChevronRight className="size-4" />
      </Link>
    </div>
  </nav>
</header>

      {/* Hero */}
      <section id="header" className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50 scroll-mt-24">
        <div className={`${container} pt-14 pb-16 md:pt-20 md:pb-24 lg:pb-28`}>
          <div className="grid items-center gap-10 md:grid-cols-2">
            <motion.div {...fadeUp}>
              <h1 className="text-4xl font-extrabold leading-tight md:text-5xl" style={{ color: brand.primary }}>
                La logística inteligente que impulsa tu negocio online en Lima
              </h1>
              <p className="mt-5 text-lg text-slate-700 max-w-xl">
                Entregas el mismo día con seguimiento en vivo, notificaciones automáticas y
                <span className="font-semibold"> pago contraentrega</span> confiable. Sube tu Excel, nosotros nos encargamos del resto.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="/#/register"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ background: brand.primary }}
                >
                  Crea tu cuenta gratis
                </a>
                <a
                  href="/#/login"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold border border-slate-300 bg-white hover:bg-slate-50"
                >
                  Agendar mi primera entrega
                </a>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-600">
                <div className="inline-flex items-center gap-2"><Clock className="size-4"/> Corte 11:00 AM → Entrega antes de 8:00 PM</div>
                <div className="inline-flex items-center gap-2"><HandCoins className="size-4"/> Contraentrega segura</div>
                <div className="inline-flex items-center gap-2"><MessageSquare className="size-4"/> WhatsApp/SMS automáticos</div>
              </div>
            </motion.div>

            {/* Visual hero: mockup panel */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
              <div className="relative rounded-2xl border border-slate-200 bg-white shadow-sm">
                {/* Header bar */}
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <span className="text-sm font-semibold">Panel de seguimiento</span>
                  <span className="text-xs text-slate-500">Tiempo real</span>
                </div>
                <div className="grid gap-0 md:grid-cols-5">
                  {/* Sidebar list */}
                  <div className="md:col-span-2 border-r border-slate-200 p-4 space-y-3">
                    {["Maria-000231", "Pedro-000232", "Ricardo-000233", "Jimena-000234"].map((code, i) => (
                      <div key={code} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Truck className="size-4" style={{ color: brand.primary }} />
                          <span>{code}</span>
                        </div>
                        <span className="rounded-md px-2 py-0.5 text-xs font-semibold" style={{ background: brand.accentGreen, color: "white" }}>En ruta</span>
                      </div>
                    ))}
                  </div>
                  {/* Map mock */}
                  <div className="md:col-span-3 relative min-h-64">
                    <HeroPanelMap
                      imageUrl={`${process.env.PUBLIC_URL}/MapaMiraflores.png`} // ← use your generated map image here
                      location="Miraflores"
                      eta="12 min"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section id="beneficios" className="border-t border-slate-100 bg-white scroll-mt-24">
        <div className={`${container} py-16 md:py-20`}>
          <motion.h2 {...fadeUp} className="text-2xl md:text-3xl font-extrabold mb-8" style={{ color: brand.primary }}>
            Beneficios clave
          </motion.h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Benefit icon={<MapPin className="size-5" />} title="Seguimiento en vivo" text="Tú y tus clientes saben dónde está cada pedido, en todo momento."/>
            <Benefit icon={<MessageSquare className="size-5" />} title="Mensajes automáticos" text="WhatsApp/SMS en cada hito, sin escribir manualmente."/>
            <Benefit icon={<HandCoins className="size-5" />} title="Pago contraentrega" text="Cobranza segura para mejorar tu flujo de caja."/>
            <Benefit icon={<Clock className="size-5" />} title="Mismo día" text="Pedidos hasta 11:00 AM → entregados antes de 8:00 PM."/>
            <Benefit icon={<Truck className="size-5" />} title="Tarifa fija Lima" text="Precio simple dentro de la cobertura, sin sorpresas."/>
            <Benefit icon={<PhoneCall className="size-5" />} title="Soporte ágil" text="Acompañamiento real cuando lo necesitas."/>
          </div>
        </div>
      </section>

      {/* Proceso */}
      <section id="proceso" className="border-t border-slate-100 bg-slate-50 scroll-mt-24">
        <div className={`${container} py-16 md:py-20`}>
          <motion.h2 {...fadeUp} className="text-2xl md:text-3xl font-extrabold mb-10" style={{ color: brand.primary }}>
            ¿Cómo funciona?
          </motion.h2>
          <div className="grid gap-6 md:grid-cols-3">
            <ProcessCard
              step="1"
              title="Sube tus pedidos"
              text="Arrastra y suelta un Excel con tus entregas en la plataforma."
              icon={<FileSpreadsheet className="size-6" />}
              badge="Carga masiva"
            />
            <ProcessCard
              step="2"
              title="Recojo inmediato"
              text="Pasamos por tu tienda o almacén para recoger los paquetes."
              icon={<Truck className="size-6" />}
              badge="Operación ágil"
            />
            <ProcessCard
              step="3"
              title="Entrega en el mismo día"
              text="Tus clientes reciben antes de las 8:00 PM. Contraentrega si aplica."
              icon={<CheckCircle2 className="size-6" />}
              badge="Cumplimiento"
            />
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center gap-3">
            <a href="/#/register" className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold text-white" style={{ background: brand.primary }}>Crear cuenta</a>
            <a href="/#/login" className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold border border-slate-300 bg-white hover:bg-slate-50">Agendar entrega</a>
          </div>
        </div>
      </section>

      {/* Tecnología */}
      <section id="tecnologia" className="border-t border-slate-100 bg-white scroll-mt-24">
        <div className={`${container} py-16 md:py-20`}>
            <motion.div {...fadeUp} className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
                <h2 className="text-2xl md:text-3xl font-extrabold" style={{ color: brand.primary }}>
                Tecnología que te da control
                </h2>

                <p className="mt-4 text-slate-700">
                Una plataforma única para operar y decidir con datos: 
                <span className="font-semibold"> resumen analítico con KPIs y conciliación</span>, 
                <span className="font-semibold"> chat centralizado (API de WhatsApp)</span> y 
                <span className="font-semibold"> mapa en vivo con rutas y ETA</span>.
                </p>

                <ul className="mt-6 space-y-3 text-slate-700">
                <li className="flex items-start gap-3">
                    <span className="mt-1 block size-2 rounded-full" style={{ background: brand.accentGreen }} />
                    <strong>Resumen analítico:</strong> OTD, rechazos, SLA, cobranzas COD, monto a depositar, comentarios y evidencias.
                </li>
                <li className="flex items-start gap-3">
                    <span className="mt-1 block size-2 rounded-full" style={{ background: brand.accentGold }} />
                    <strong>Chat (WhatsApp):</strong> conversaciones unificadas, plantillas, seguimiento de la ventana de 24h.
                </li>
                <li className="flex items-start gap-3">
                    <span className="mt-1 block size-2 rounded-full" style={{ background: brand.primary }} />
                    <strong>Mapa en vivo:</strong> conductores y puntos en tiempo real, rutas, estados y ETA.
                </li>
                </ul>
            </div>

            {/* Showcase (tabs + slider) */}
            <TechShowcase />
            </motion.div>
        </div>
        </section>

      {/* Cobertura */}
      <section id="cobertura" className="border-t border-slate-100 bg-slate-50 scroll-mt-24">
        <div className={`${container} py-16 md:py-20`}>
          <motion.h2 {...fadeUp} className="text-2xl md:text-3xl font-extrabold mb-8" style={{ color: brand.primary }}>
            Cobertura Lima Metropolitana & Callao
          </motion.h2>

          <div className="grid gap-8 md:grid-cols-2 md:items-start">
            {/* Summary card (replaces the map placeholder) */}
            <motion.div {...fadeUp} className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm overflow-hidden">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,#f8fafc,transparent_55%),radial-gradient(circle_at_80%_70%,#eef2ff,transparent_55%)]" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-lg bg-white/90 px-3 py-2 shadow">
                  <MapPin className="size-4" style={{ color: brand.primary }} />
                  <span className="text-sm font-semibold">37+ distritos cubiertos</span>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <Kpi label="Tarifa" value="Fija en cobertura" icon={<Truck className="size-4" />} color={brand.primary} />
                  <Kpi label="Corte mismo día" value="11:00 AM" icon={<Clock className="size-4" />} color={brand.accentGold} />
                  <Kpi label="Entrega" value="Antes de 8:00 PM" icon={<CheckCircle2 className="size-4" />} color={brand.accentGreen} />
                  <Kpi label="Contraentrega" value="Disponible" icon={<HandCoins className="size-4" />} color={brand.primary} />
                </div>

                <p className="mt-5 text-slate-700">
                  Entregamos en toda el área metropolitana. Pedidos ingresados antes de <strong>11:00 AM</strong> se entregan
                  <strong> el mismo día</strong> antes de <strong>8:00 PM</strong>.
                </p>
              </div>
            </motion.div>

            {/* All districts + filter */}
            <CoverageList />
          </div>
        </div>
      </section>

      {/* Resultados / Confiabilidad */}
      <section className="border-t border-slate-100 bg-white">
        <div className={`${container} py-16 md:py-20`}>
          <div className="grid gap-8 md:grid-cols-3">
            <StatCard value="95%" label="Entregas a tiempo (OTD)" accent={brand.accentGreen} />
            <StatCard value="3–5" label="Promedio de rechazos/25 envíos (por producto)" accent={brand.accentGold} />
            <StatCard value="24/7" label="Panel y seguimiento en línea" accent={brand.primary} />
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center gap-3">
            <a href="/#/register" className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold text-white" style={{ background: brand.primary }}>Crear cuenta</a>
            <a href={`https://wa.me/51977689676?text=${encodeURIComponent("Hola Fast Max")}`} target="_blank" rel="noopener noreferrer" aria-label="Chatear por WhatsApp con Fast Max" className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold border border-slate-300 bg-white hover:bg-slate-50">Hablar con ventas</a>
                 
          </div>
        </div>
      </section>

      <section id="precios" className="border-t border-slate-100 bg-slate-50 scroll-mt-24">
        <div className={`${container} py-16 md:py-20`}>
          <motion.h2 {...fadeUp} className="text-2xl md:text-3xl font-extrabold text-center" style={{ color: brand.primary }}>
            Tarifas simples por entrega
          </motion.h2>
          <motion.p {...fadeUp} transition={{ delay: 0.05 }} className="mt-3 text-center text-slate-700">
            Sin costo de plataforma. Paga solo por entrega dentro de la cobertura.
          </motion.p>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {/* Plan 1 */}
            <PriceCard
              title="1 envío"
              subtitle="Ideal para empezar"
              price="S/ 15"
              suffix="c/u"
              features={[
                "Tarifa fija en cobertura",
                "Seguimiento en vivo",
                "Mensajes automáticos (WhatsApp)",
                "Contraentrega disponible",
                "Corte 09:00 AM → mismo día"
              ]}
              ctaText="Crear cuenta"
              ctaTo="/register"
              secondaryText="Hablar con ventas"
              secondaryHref={WA_HREF}   // ← WhatsApp link
            />

            {/* Plan 2 (most popular) */}
            <PriceCard
              title="2+ envíos por día"
              subtitle="Optimiza tu costo diario"
              price="S/ 10.90"
              suffix="c/u"
              popular
              features={[
                "Tarifa fija en cobertura",
                "Dashboard y reportes",
                "Mensajes automáticos (WhatsApp)",
                "Contraentrega disponible",
                "Corte 09:00 AM → mismo día"
              ]}
              ctaText="Crear cuenta"
              ctaTo="/register"
              secondaryText="Solicitar asesoría"
              secondaryHref={WA_HREF}   // ← WhatsApp link
            />

            {/* Plan 3 */}
            <PriceCard
              title="150+ envíos por semana"
              subtitle="Alto volumen semanal"
              price="S/ 8.90"
              suffix="c/u"
              features={[
                "Cuenta maestra multi-tienda",
                "Subcuentas con roles por tienda",
                "Analítica por tienda y global",
                "Inventario completo + conciliación",
                "WhatsApp y tracking en vivo",
                "Corte 11:00 → mismo día · COD"
              ]}
              ctaText="Crear cuenta"
              ctaTo="/register"
              secondaryText="Solicitar asesoría"
              secondaryHref={WA_HREF}   // ← WhatsApp link
            />
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            * Aplica en Lima Metropolitana y Callao. Tarifas por entrega dentro de cobertura. Para zonas especiales o
            necesidades personalizadas, contáctanos.
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative isolate overflow-hidden" style={{ background: brand.primary }}>
        <div className={`${container} py-12 md:py-14 text-white`}>
          <div className="flex flex-col items-center text-center">
            <h3 className="text-2xl md:text-3xl font-extrabold">Simplifica tu logística hoy</h3>
            <p className="mt-2 text-white/90 max-w-2xl">Únete a FAST MAX y enfócate en vender. Nosotros entregamos con tecnología, velocidad y transparencia.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold text-slate-900"
                  style={{ background: brand.accentGold }}
                >
                  Crear cuenta
                  <ChevronRight className="size-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold border border-white/30">
                  Agendar entrega
                </Link>
              
        
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" className="border-t border-slate-100 bg-white">
        <div className={`${container} py-12 grid gap-10 md:grid-cols-4`}>
          <div className="col-span-2">
            <a href="" onClick={scrollTo("header")} className="flex items-center gap-3" aria-label="FAST MAX">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="FAST MAX"
                className="h-8 w-auto"
            />
              <span className="text-lg font-extrabold tracking-wide" style={{ color: brand.primary }}>FAST MAX</span>
            </a>
            <p className="mt-4 max-w-md text-sm text-slate-600">
              Plataforma logística para vendedores online en Lima. Entregas el mismo día con seguimiento en vivo, notificaciones automáticas y pago contraentrega.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold">Enlaces</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li><a href="#beneficios" onClick={scrollTo("beneficios")} className="hover:opacity-80">Beneficios</a></li>
              <li><a href="#proceso" onClick={scrollTo("proceso")} className="hover:opacity-80">Cómo funciona</a></li>
              <li><a href="#tecnologia" onClick={scrollTo("tecnologia")} className="hover:opacity-80">Tecnología</a></li>
              <li><a href="#cobertura" onClick={scrollTo("cobertura")} className="hover:opacity-80">Cobertura</a></li>
              <li><a href="#precios" onClick={scrollTo("precios")} className="hover:opacity-80">Precios</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold">Contacto</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li>Email: contacto@fastmax.pe</li>
              <li>
                <a
                  href={`https://wa.me/51977689676?text=${encodeURIComponent("Hola Fast Max")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chatear por WhatsApp con Fast Max"
                  className="inline-flex items-center gap-2 font-semibold text-green-600 hover:underline"
                >
                  WhatsApp: +51 977 689 676
                </a>
              </li>
              <li>Dirección: Los Mirtos 145 Lima, Perú</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-100 py-6">
          <div className={`${container} flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500`}>
            <p>© {new Date().getFullYear()} FAST MAX. Todos los derechos reservados.</p>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="hover:opacity-80">Términos</Link>
              <Link to="/privacy" className="hover:opacity-80">Privacidad</Link>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Floating WhatsApp button */}
      <WhatsAppFloat phone="+51 977 689 676" />
    </div>
  );
}

function Benefit({ icon, title, text }) {
  return (
    <motion.div {...fadeUp} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="mt-1 grid size-10 place-items-center rounded-xl bg-slate-50 text-slate-700">
          {icon}
        </div>
        <div>
          <h3 className="text-base font-semibold">{title}</h3>
          <p className="mt-1 text-sm text-slate-600">{text}</p>
        </div>
      </div>
    </motion.div>
  );
}

function ProcessCard({ step, title, text, icon, badge }) {
  return (
    <motion.div {...fadeUp} className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="absolute -top-3 left-6 rounded-full px-2 py-0.5 text-xs font-semibold text-white" style={{ background: brand.primary }}>Paso {step}</div>
      <div className="flex items-start gap-4">
        <div className="grid size-12 place-items-center rounded-xl bg-slate-50 text-slate-700">{icon}</div>
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="mt-1 text-sm text-slate-600">{text}</p>
          <span className="mt-3 inline-flex rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{badge}</span>
        </div>
      </div>
    </motion.div>
  );
}

function StatCard({ value, label, accent }) {
  return (
    <motion.div {...fadeUp} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
      <div className="text-4xl font-extrabold" style={{ color: accent }}>{value}</div>
      <div className="mt-2 text-sm text-slate-600">{label}</div>
    </motion.div>
  );
}

function TechShowcase({ images }) {
  // Put your 3 screenshots in /public or pass URLs via `images={...}`
  const defaults = {
    analytics: `${process.env.PUBLIC_URL}/dashboard.png`, // resumen analítico (KPIs + conciliación)
    chat:      `${process.env.PUBLIC_URL}/ChatView.png`,       // bandeja WhatsApp
    map:       `${process.env.PUBLIC_URL}/MapView.png`,        // mapa en vivo
  };

  const shots = {
    analytics: images?.analytics || defaults.analytics,
    chat:      images?.chat      || defaults.chat,
    map:       images?.map       || defaults.map,
  };

  const tabs = useMemo(
    () => ([
      { key: "analytics", label: "Resumen analítico", img: shots.analytics, chip: "OTD · COD · Depósitos" },
      { key: "chat",      label: "Chat (WhatsApp)",   img: shots.chat,      chip: "Conversaciones unificadas" },
      { key: "map",       label: "Mapa en vivo",      img: shots.map,       chip: "Rutas · ETA · Estados" },
    ]),
    [shots.analytics, shots.chat, shots.map]
  );

  const [idx, setIdx] = useState(0);
  const next = () => setIdx((i) => (i + 1) % tabs.length);
  const prev = () => setIdx((i) => (i - 1 + tabs.length) % tabs.length);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const active = tabs[idx];

  return (
    <div className="relative">
      {/* Tabs */}
      <div className="mb-3 flex flex-wrap gap-2">
        {tabs.map((t, i) => {
          const isActive = i === idx;
          return (
            <button
              key={t.key}
              onClick={() => setIdx(i)}
              className={`rounded-xl px-3 py-1.5 text-sm font-semibold border transition
                ${isActive
                  ? "border-transparent text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
              style={isActive ? { background: brand.primary } : {}}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Frame */}
      <div className="relative rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <motion.div
          key={active.key}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="relative overflow-hidden rounded-xl"
        >
          <img
            src={active.img}
            alt={active.label}
            className="h-72 w-full object-cover md:h-80"
            loading="lazy"
            decoding="async"
          />

          {/* Overlay decorations */}
          <div className="absolute left-3 top-3 rounded-md bg-white/90 px-2 py-1 text-xs font-semibold shadow">
            {active.chip}
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 p-3">
            <div className="flex flex-wrap gap-2">
              <Chip label="Tiempo real" color={brand.accentGreen} />
              {active.key === "analytics" && (
                <>
                  <Chip label="OTD 95%" color={brand.accentGold} />
                  <Chip label="Depósitos del día" color={brand.primary} />
                </>
              )}
              {active.key === "chat" && <Chip label="Plantillas aprobadas" color={brand.primary} />}
              {active.key === "map" && <Chip label="ETA 12 min" color={brand.accentGold} />}
            </div>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/25 to-transparent" />
        </motion.div>

        {/* Arrows */}
        <button
          onClick={prev}
          aria-label="Anterior"
          className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow hover:bg-white"
        >
          ‹
        </button>
        <button
          onClick={next}
          aria-label="Siguiente"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow hover:bg-white"
        >
          ›
        </button>

        {/* Dots */}
        <div className="mt-3 flex justify-center gap-2">
          {tabs.map((_, i) => (
            <span
              key={i}
              onClick={() => setIdx(i)}
              className={`inline-block h-1.5 w-4 cursor-pointer rounded-full transition
                ${i === idx ? "bg-slate-900" : "bg-slate-300 hover:bg-slate-400"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Chip({ label, color }) {
  return (
    <span
      className="pointer-events-auto inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white shadow"
      style={{ background: color }}
    >
      {label}
    </span>
  );
}

function HeroPanelMap({ imageUrl, location = "Miraflores", eta = "12 min" }) {
  return (
    <div className="absolute inset-0 rounded-tr-2xl rounded-br-2xl overflow-hidden">
      {/* Background: image if available, else SVG fallback */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`Mapa de ${location} con ruta activa`}
          className="h-full w-full object-cover"
        />
      ) : (
        <MiniMapSVG />
      )}

      {/* Overlay: city tag (top-left) */}
      <div className="absolute left-6 top-6 inline-flex items-center gap-2 rounded-lg bg-white/90 px-3 py-2 shadow">
        <MapPin className="size-4" style={{ color: brand.primary }} />
        <span className="text-sm font-semibold">{location}</span>
      </div>

      {/* Overlay: ETA (bottom-right) */}
      <div className="absolute right-6 bottom-6 inline-flex items-center gap-2 rounded-lg bg-white/90 px-3 py-2 shadow">
        <Clock className="size-4" />
        <span className="text-sm">ETA {eta}</span>
      </div>

      {/* Route overlay (animated) */}
      <svg className="pointer-events-none absolute inset-0" viewBox="0 0 800 480" preserveAspectRatio="none">
        {/* Path from driver (left/bottom) to destination (center/top-right) */}
        <motion.path
          d="M80,360 C180,320 260,300 360,260 C520,200 620,180 700,140"
          fill="none"
          stroke={brand.primary}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="8 10"
          vectorEffect="non-scaling-stroke"
          // draw -> hold -> hide -> pause -> loop
          initial={{ pathLength: 0, opacity: 1 }}
          animate={{ pathLength: [0, 1, 1, 0], opacity: [1, 1, 0.6, 0] }}
          transition={{
            duration: 1.6,            // draw + hold + hide time
            ease: "easeInOut",
            times: [0, 0.7, 0.85, 1], // control when each keyframe happens
            repeat: Infinity,
            repeatType: "loop",
            repeatDelay: 2            // <- pause before re-drawing
          }}
        />

        {/* Destination pin */}
        <g transform="translate(700,140)">
          <circle r="14" fill={brand.accentGreen} />
          <circle r="20" fill="white" opacity="0.9" />
          <circle r="14" fill={brand.accentGreen} />
        </g>

        {/* Driver marker with soft pulse */}
        <g transform="translate(80,360)">
          <circle r="16" fill={brand.primary} opacity="0.25">
            <animate
              attributeName="r"
              values="16;22;16"
              dur="1.8s"
              repeatCount="indefinite"
            />
          </circle>
          <circle r="12" fill={brand.primary} />
        </g>
      </svg>
    </div>
  );
}

function MiniMapSVG() {
  return (
    <svg className="h-full w-full" viewBox="0 0 800 480" preserveAspectRatio="none" role="img" aria-label="Mini mapa con ruta activa">
      {/* soft background */}
      <defs>
        <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#f8fafc" />
          <stop offset="1" stopColor="#eef2ff" />
        </linearGradient>
      </defs>
      <rect width="800" height="480" fill="url(#bg)" />
      {/* faux streets */}
      <g stroke="#e2e8f0" strokeWidth="2">
        <path d="M0 80 H800" />
        <path d="M0 160 H800" />
        <path d="M0 240 H800" />
        <path d="M0 320 H800" />
        <path d="M0 400 H800" />
        <path d="M120 0 V480" />
        <path d="M260 0 V480" />
        <path d="M420 0 V480" />
        <path d="M620 0 V480" />
      </g>
      {/* (The route/markers are drawn by the parent overlay) */}
    </svg>
  );
}

function Kpi({ label, value, icon, color }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
      <span className="grid size-8 place-items-center rounded-lg" style={{ background: "#F8FAFC", color }}>
        {icon}
      </span>
      <div className="leading-tight">
        <div className="text-[11px] text-slate-500">{label}</div>
        <div className="text-sm font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function CoverageList() {
  const [q, setQ] = useState("");

  // Full, deduped, normalized list (≈43). Sorted for predictable search results.
  const allDistricts = useMemo(() => {
    const arr = [
      "Cercado de Lima","Jesús María","Lince","Magdalena del Mar","Pueblo Libre","Breña","San Miguel",
      "San Isidro","San Borja","Miraflores",
      "Callao","Bellavista","La Perla","La Punta","Carmen de la Legua",
      "Comas","Independencia","San Martín de Porres","Los Olivos","Rímac",
      "Puente Piedra","Carabayllo","Ancón","Santa Rosa","Ventanilla",
      "Ate","Santa Anita","El Agustino","San Juan de Lurigancho","La Molina",
      "Chorrillos","San Juan de Miraflores","Villa El Salvador","Villa María del Triunfo",
      "Barranco","Santiago de Surco","Surquillo"
    ];
    return Array.from(new Set(arr)).sort((a, b) => a.localeCompare(b, "es"));
  }, []);

  const matches = (name) => name.toLowerCase().includes(q.trim().toLowerCase());

  // When there's no query, show a random 11 (stable per mount); when filtering, show top matches.
  const randomOrder = useMemo(() => {
    const copy = [...allDistricts];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, [allDistricts]);

  const baseList = q ? allDistricts.filter(matches) : randomOrder;
  const visible = baseList.slice(0, 11);              // 11 districts
  const remaining = Math.max(baseList.length - visible.length, 0); // + tail item

  return (
    <div>
      {/* Search */}
      <div className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2">
        <Search className="size-4 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar distrito…"
          className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
        />
      </div>

      {/* Results */}
      {baseList.length === 0 ? (
        <div className="mt-6 text-sm text-slate-600">
          No encontramos tu distrito.{" "}
          <a href="#contacto" onClick={scrollTo("contacto")} className="font-semibold underline underline-offset-4">
            Contáctanos
          </a>
          .
        </div>
      ) : (
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {visible.map((d) => (
            <li
              key={d}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <span className="size-2 rounded-full" style={{ background: brand.accentGreen }} />
              {d}
            </li>
          ))}
          {/* Tail item: shows remaining count (e.g., "32 distritos más…") */}
          {remaining > 0 && (
            <li className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-500">
              {remaining} distritos más…
            </li>
          )}
        </ul>
      )}

      <div className="mt-6 text-sm text-slate-600">
        ¿Tu distrito no aparece?{" "}
        <a href="#contacto" onClick={scrollTo("contacto")} className="font-semibold underline underline-offset-4">
          Contáctanos
        </a>
        .
      </div>
    </div>
  );
}


function PriceCard({
  title,
  subtitle,
  price,
  suffix = "c/u",
  popular = false,
  features = [],
  ctaText = "Crear cuenta",
  ctaTo = "/register",
  secondaryText = "Hablar con ventas",
  secondaryHref,           // ← NEW
  onSecondaryClick,        // (kept for backward compatibility)
}) {
  return (
    <motion.div
      {...fadeUp}
      className={`relative rounded-2xl border bg-white p-6 shadow-sm ${
        popular ? "border-transparent" : "border-slate-200"
      }`}
      style={popular ? { boxShadow: "0 10px 25px rgba(177, 51, 110, 0.12)" } : {}}
    >
      {popular && (
        <div
          className="absolute -top-3 left-6 rounded-full px-2 py-0.5 text-xs font-semibold text-white"
          style={{ background: brand.primary }}
        >
          Más popular
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-extrabold">{title}</h3>
        <p className="text-sm text-slate-600">{subtitle}</p>
      </div>

      <div className="mb-5 flex items-end gap-2">
        <div className="text-3xl font-extrabold" style={{ color: brand.primary }}>{price}</div>
        <div className="pb-1 text-sm text-slate-600">{suffix}</div>
      </div>

      <ul className="mb-6 space-y-2 text-sm text-slate-700">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="mt-1 inline-block size-2 rounded-full" style={{ background: brand.accentGreen }} />
            {f}
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-2">
        <Link
          to={ctaTo}  // with HashRouter, this should be "/register"
          className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm"
          style={{ background: brand.primary }}
        >
          {ctaText}
        </Link>

        {secondaryHref ? (
          <a
            href={secondaryHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            {secondaryText}
          </a>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onSecondaryClick && onSecondaryClick(e);
            }}
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            {secondaryText}
          </button>
        )}
      </div>
    </motion.div>
  );
}

function WhatsAppFloat({ phone = "+51 977 689 676" }) {
  // Clean number -> 51977689676
  const digits = String(phone).replace(/\D/g, "");
  const href = `https://wa.me/${digits}?text=${encodeURIComponent("Hola Fast Max, quisiera más información.")}`;

  return (
    <div className="fixed bottom-4 right-4 z-50 md:bottom-6 md:right-6">
      {/* group wrapper so tooltip can react to hover, even outside the anchor box */}
      <div className="group relative">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chatear por WhatsApp con Fast Max"
          className="relative inline-flex items-center justify-center"
        >
          {/* ripple halo */}
          <span
            className="absolute -inset-1 rounded-full animate-ping"
            style={{ background: "rgba(103,164,60,0.35)" }} // brand.accentGreen w/ alpha
          />
          {/* main button */}
          <span
            className="relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-transform group-hover:scale-105"
            style={{ background: "#67A43C" }} // brand.accentGreen
          >
            <WhatsAppIcon sx={{ fontSize: 28, color: "#fff" }} />
          </span>
        </a>

        {/* tooltip (desktop only). pointer-events-none to avoid flicker */}
        <div className="pointer-events-none absolute right-[calc(100%+12px)] top-1/2 -translate-y-1/2 opacity-0 translate-x-2 transition md:group-hover:opacity-100 md:group-hover:translate-x-0">
          <div className="rounded-xl bg-slate-900/90 px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
            Chatea con Ventas
          </div>
        </div>
      </div>
    </div>
  );
}
